import type { RequestContext } from "../context/request-context.js";
import type { AppVersion } from "../../shared/branding/app-version.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { PermissionCode } from "../../domain/identity/access-types.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import type { JsonObject } from "../../shared/kernel/json.js";
import {
  AUDIT_EVENT_OF_SECTION,
  LOCKED_RULES,
  SECTIONS_REQUIRING_REASON,
  defaultSystemSettings,
  diffSection,
  formatCnpj,
  historyStatus,
  historyValue,
  isValidCnpj,
  maskSecret,
  normalizeCnpj,
  validateSection,
  type EditableSection,
  type IntegrationTestResult,
  type LockedRule,
  type SettingsHistoryEntry,
  type SettingsSectionId,
  type SystemSettings
} from "../../domain/settings/system-settings.js";
import {
  NON_CONFIGURABLE_NUMBERING,
  numberingWarnings,
  previewDocumentNumber,
  validateNumberingConfig,
  type NumberingConfig
} from "../../domain/settings/document-numbering.js";
import {
  PLH_APPROVAL_STATUS_LABELS,
  documentTypesRequiringPlhApproval,
  type PlhApprovalStatus
} from "../../domain/review/plh-approval-policy.js";
import type { SettingsSecretStore, SystemSettingsRepository } from "../../repositories/system-settings-repository.js";
import type { SystemSettingsProvider } from "./system-settings-provider.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** O segredo da integração ICP no cofre. Um só nome, conhecido só daqui. */
const ICP_SECRET_NAME = "icp.clientSecret";

/** Permissão de LEITURA e de ALTERAÇÃO de cada seção. */
const SECTION_PERMISSIONS: Record<SettingsSectionId, { read: PermissionCode; update: PermissionCode }> = {
  general: { read: PermissionCode.SettingsRead, update: PermissionCode.SettingsUpdate },
  numbering: { read: PermissionCode.SettingsRead, update: PermissionCode.SettingsUpdate },
  workflow: { read: PermissionCode.SettingsRead, update: PermissionCode.SettingsWorkflowUpdate },
  inspection: { read: PermissionCode.SettingsRead, update: PermissionCode.SettingsUpdate },
  end: { read: PermissionCode.SettingsRead, update: PermissionCode.SettingsUpdate },
  integrity: { read: PermissionCode.SettingsRead, update: PermissionCode.SettingsUpdate },
  import: { read: PermissionCode.SettingsRead, update: PermissionCode.SettingsImportUpdate },
  notification: { read: PermissionCode.SettingsRead, update: PermissionCode.SettingsUpdate },
  files: { read: PermissionCode.SettingsRead, update: PermissionCode.SettingsUpdate },
  security: { read: PermissionCode.SettingsSecurityRead, update: PermissionCode.SettingsSecurityUpdate },
  signature: { read: PermissionCode.SettingsSignatureRead, update: PermissionCode.SettingsSignatureUpdate },
  integration: { read: PermissionCode.SettingsIntegrationRead, update: PermissionCode.SettingsIntegrationUpdate }
};

export interface SettingsDiagnosticsProbe {
  readonly mode: "NAVEGADOR" | "SERVIDOR";
  appVersion(): string;
  /**
   * VERSÃO-05: o Diagnóstico ("Sobre o Sistema") precisa de nome, versão de
   * exibição, ambiente e status de release como CAMPOS SEPARADOS — nunca
   * embutidos num único texto formatado (isso é o que `appVersion()` já faz,
   * e continua existindo só para não quebrar quem já lê aquele texto). Os
   * dois probes montam este objeto com a MESMA chamada a `resolveAppVersion`
   * que já usam para formatar `appVersion()`.
   */
  versionInfo(): AppVersion;
  database(): Promise<{ engine: string; detail: string; currentMigration: string | null; pendingMigrations: string[] | null; note: string }>;
  pdf(): Promise<{ ok: boolean; message: string }>;
}

export interface SettingsView {
  /** Só as seções que a pessoa pode ler; segredo nunca, em nenhuma. */
  settings: Partial<SystemSettings>;
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
  readable: SettingsSectionId[];
  editable: SettingsSectionId[];
  secretStore: { available: boolean; reason: string };
  mode: "NAVEGADOR" | "SERVIDOR";
}

export interface OperationalSettings {
  general: SystemSettings["general"];
  files: SystemSettings["files"];
  end: SystemSettings["end"];
  inspection: Pick<SystemSettings["inspection"], "defaultNature" | "defaultModality">;
}

export interface CheckItem {
  label: string;
  status: "OK" | "FALHA" | "NÃO VERIFICADO" | "AVISO";
  detail: string;
}

export interface DiagnosticResult {
  id: string;
  label: string;
  ok: boolean;
  status: string;
  message: string;
  checks: CheckItem[];
}

export interface IntegrationView {
  id: "ICP" | "EMAIL" | "STORAGE" | "PDF";
  name: string;
  type: string;
  status: string;
  environment: string;
  integrated: boolean;
  canDeactivate: boolean;
  editTab: string | null;
  note: string;
  lastTest: IntegrationTestResult | null;
}

export interface NumberingPreview {
  ok: boolean;
  preview: string;
  errors: string[];
  warnings: string[];
}

export interface WorkflowOverview {
  documentTypes: Array<{ type: string; label: string; requiresPlh: boolean; approvalFinalizes: boolean }>;
  flow: Array<{ status: PlhApprovalStatus; label: string }>;
  lockedRules: LockedRule[];
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  INTEGRITY: "Relatório de Integridade (RI)", ME: "Medição de Espessura", LP: "Líquido Penetrante", PM: "Partículas Magnéticas",
  USC: "Ultrassom Convencional", BSCAN: "B-Scan / C-Scan", AVR: "AVR"
};
/** Tipos cujo "APROVAR" já é o fechamento (flag `finalizesOnApproval` do adaptador). */
const APPROVAL_FINALIZES = new Set(["ME", "BSCAN"]);

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/** Só as chaves que o padrão conhece — campo desconhecido não entra no registro. */
function pickShape<T>(shape: T, value: unknown): T {
  if (!isObject(shape)) return (value === undefined ? shape : value) as T;
  if (!isObject(value)) return shape;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(shape as Record<string, unknown>)) {
    out[key] = key in value ? pickShape((shape as Record<string, unknown>)[key], value[key]) : (shape as Record<string, unknown>)[key];
  }
  return out as T;
}

/**
 * CONFIGURAÇÕES — fachada exposta às telas (e ao `/api/rpc` no modo servidor).
 * Toda leitura e toda gravação confere a permissão do ato; o registro é
 * gravado por versão (duas pessoas salvando ao mesmo tempo não se
 * sobrescrevem em silêncio); cada alteração vira linha no histórico e evento
 * de auditoria.
 */
export interface DatabaseResetResult {
  readonly success: boolean;
  readonly backupPath: string;
  readonly backupSizeBytes: number;
  readonly deletedCounts: Record<string, number>;
  readonly executedAt: string;
  readonly executedBy: string;
}

export class SystemSettingsService {
  private readonly auth = new RbacAuthorizationService();
  private userNameOf: ((userId: string) => Promise<string | null>) | null = null;
  private databaseResetHandler: ((context: RequestContext, reason?: string) => Promise<DatabaseResetResult>) | null = null;
  private adminAuthResolver: ((identifier: string, password: string) => Promise<{ user?: any }>) | null = null;

  constructor(
    private readonly provider: SystemSettingsProvider,
    private readonly repo: SystemSettingsRepository,
    private readonly secrets: SettingsSecretStore,
    private readonly probe: SettingsDiagnosticsProbe,
    private readonly audit?: AuditRecorder
  ) {}

  attachUserNameResolver(resolver: (userId: string) => Promise<string | null>): void {
    this.userNameOf = resolver;
  }

  attachDatabaseResetHandler(
    handler: (context: RequestContext, reason?: string) => Promise<DatabaseResetResult>,
    adminAuth: (identifier: string, password: string) => Promise<{ user?: any }>
  ): void {
    this.databaseResetHandler = handler;
    this.adminAuthResolver = adminAuth;
  }

  // ═══════════════════════════════════════════════════════════ leitura

  async getSettings(context: RequestContext): Promise<SettingsView> {
    this.auth.require(context, PermissionCode.SettingsRead);
    const { settings, version } = await this.provider.reload();
    const readable = (Object.keys(SECTION_PERMISSIONS) as SettingsSectionId[]).filter(s => this.auth.can(context, SECTION_PERMISSIONS[s].read));
    const editable = readable.filter(s => s !== "workflow" && s !== "integration" && this.auth.can(context, SECTION_PERMISSIONS[s].update));
    const view: Partial<SystemSettings> = {};
    const tests = await this.repo.listIntegrationTests();
    for (const section of readable) {
      if (section === "workflow") continue;
      (view as Record<string, unknown>)[section] = structuredClone(settings[section]);
    }
    // Último teste vem do registro operacional (fora da versão).
    if (view.signature) view.signature.lastTest = tests["ICP"] ?? null;
    if (view.integration) view.integration = { lastTests: tests };
    return {
      settings: view, version, updatedAt: settings.updatedAt, updatedBy: settings.updatedBy, readable, editable,
      secretStore: { available: this.secrets.available, reason: this.secrets.unavailableReason },
      mode: this.probe.mode
    };
  }

  /**
   * O subconjunto que as telas de trabalho precisam (autosave, limites de
   * arquivo, padrões de END) — para QUALQUER pessoa autenticada. Não inclui
   * segurança, assinatura, integração nem numeração.
   */
  async getOperationalSettings(context: RequestContext): Promise<OperationalSettings> {
    if (!context.userId) throw new AppError(ErrorCode.Authentication, "Not authenticated", { safeMessage: "Sessão não autenticada." });
    const s = await this.provider.get();
    return { general: structuredClone(s.general), files: structuredClone(s.files), end: structuredClone(s.end), inspection: { defaultNature: s.inspection.defaultNature, defaultModality: s.inspection.defaultModality } };
  }

  async getWorkflowOverview(context: RequestContext): Promise<WorkflowOverview> {
    this.auth.require(context, PermissionCode.SettingsRead);
    const flow: PlhApprovalStatus[] = ["EM_ELABORACAO", "AGUARDANDO_ANALISE_PLH", "EM_ANALISE_PLH", "CORRECAO_SOLICITADA", "AGUARDANDO_REANALISE_PLH", "APROVADO_PLH"];
    return {
      documentTypes: documentTypesRequiringPlhApproval().map(t => ({ type: String(t), label: DOCUMENT_TYPE_LABELS[String(t)] ?? String(t), requiresPlh: true, approvalFinalizes: APPROVAL_FINALIZES.has(String(t)) })),
      flow: flow.map(status => ({ status, label: PLH_APPROVAL_STATUS_LABELS[status] })),
      lockedRules: [...LOCKED_RULES]
    };
  }

  async previewNumbering(context: RequestContext, config: NumberingConfig): Promise<NumberingPreview> {
    this.auth.require(context, PermissionCode.SettingsRead);
    const errors = validateNumberingConfig(config);
    if (errors.length > 0) return { ok: false, preview: "", errors, warnings: [] };
    try {
      return { ok: true, preview: previewDocumentNumber(config), errors: [], warnings: numberingWarnings(config) };
    } catch (e) {
      return { ok: false, preview: "", errors: [(e as Error).message], warnings: [] };
    }
  }

  listNonConfigurableNumbering(context: RequestContext): typeof NON_CONFIGURABLE_NUMBERING {
    this.auth.require(context, PermissionCode.SettingsRead);
    return NON_CONFIGURABLE_NUMBERING;
  }

  async listHistory(context: RequestContext, limit = 500): Promise<Array<SettingsHistoryEntry & { status: "VIGENTE" | "SUBSTITUÍDA" }>> {
    this.auth.require(context, PermissionCode.SettingsAuditRead);
    const entries = await this.repo.listHistory(Math.min(Math.max(1, limit), 2000));
    const status = historyStatus(entries);
    return entries.map(e => ({ ...e, status: status.get(e.id) ?? "VIGENTE" }));
  }

  // ═══════════════════════════════════════════════════════════ gravação

  async updateSection(context: RequestContext, section: SettingsSectionId, value: unknown, expectedVersion: number, reason: string): Promise<SettingsView> {
    const permissions = SECTION_PERMISSIONS[section];
    if (!permissions) throw new AppError(ErrorCode.Validation, "Unknown section", { safeMessage: "Seção de configuração desconhecida." });
    this.auth.require(context, permissions.update);
    if (section === "workflow") {
      throw new AppError(ErrorCode.Validation, "Workflow is governance", { safeMessage: "O workflow não tem parâmetro editável: todos os itens são regras de governança da aprovação pelo PLH (veja a lista travada nesta aba)." });
    }
    if (section === "integration") {
      throw new AppError(ErrorCode.Validation, "Integration is derived", { safeMessage: "As integrações são alteradas pelas ações da própria lista (testar, ativar/desativar) ou na aba de cada serviço." });
    }
    const cleanReason = this.requireReason(section, reason);
    const { settings: current, version } = await this.provider.reload();
    this.assertVersion(expectedVersion, version);

    const errors = validateSection(section as EditableSection, value);
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Invalid settings", { safeMessage: errors.join(" ") });

    const defaults = defaultSystemSettings();
    let nextSection = pickShape(defaults[section as EditableSection], value) as unknown;
    if (section === "import") {
      const v = nextSection as SystemSettings["import"];
      nextSection = {
        ...v,
        aliases: v.aliases.map(a => ({ field: String(a.field).trim(), alias: String(a.alias).trim() })),
        extraEmptyMarkers: [...new Set(v.extraEmptyMarkers.map(m => String(m).trim()))]
      };
    }
    if (section === "files") {
      const v = nextSection as SystemSettings["files"];
      nextSection = { ...v, allowedImageExtensions: [...new Set(v.allowedImageExtensions)] };
    }
    if (section === "signature") {
      const v = nextSection as SystemSettings["signature"];
      // Máscara, data da troca e último teste não são editáveis por aqui.
      nextSection = { ...v, company: { ...v.company, cnpj: normalizeCnpj(v.company.cnpj), legalName: v.company.legalName.trim(), tradeName: v.company.tradeName.trim() }, secretMask: current.signature.secretMask, secretUpdatedAt: current.signature.secretUpdatedAt, lastTest: null };
    }

    const changes = diffSection(current[section as EditableSection], nextSection, section);
    if (changes.length === 0) return this.getSettings(context);

    const next: SystemSettings = { ...current, [section]: nextSection };
    await this.persist(context, next, version, section, changes, cleanReason);
    return this.getSettings(context);
  }

  /** Troca o segredo da ICP. O valor vai direto para o cofre; o registro guarda só a máscara. */
  async replaceIcpSecret(context: RequestContext, secret: string, expectedVersion: number, reason: string): Promise<SettingsView> {
    this.auth.require(context, PermissionCode.SettingsSignatureUpdate);
    const cleanReason = this.requireReason("signature", reason);
    const value = String(secret ?? "");
    if (value.trim().length < 8) throw new AppError(ErrorCode.Validation, "Secret too short", { safeMessage: "O segredo informado é curto demais (mínimo 8 caracteres)." });
    if (!this.secrets.available) throw new AppError(ErrorCode.Validation, "Secret store unavailable", { safeMessage: this.secrets.unavailableReason });
    const { settings: current, version } = await this.provider.reload();
    this.assertVersion(expectedVersion, version);
    await this.secrets.put(ICP_SECRET_NAME, value);
    const mask = maskSecret(value);
    const next: SystemSettings = { ...current, signature: { ...current.signature, secretMask: mask, secretUpdatedAt: new Date().toISOString() } };
    await this.persist(context, next, version, "signature", [{ path: "signature.secret", previous: current.signature.secretMask ?? "—", next: mask }], cleanReason);
    return this.getSettings(context);
  }

  async removeIcpSecret(context: RequestContext, expectedVersion: number, reason: string): Promise<SettingsView> {
    this.auth.require(context, PermissionCode.SettingsSignatureUpdate);
    const cleanReason = this.requireReason("signature", reason);
    const { settings: current, version } = await this.provider.reload();
    this.assertVersion(expectedVersion, version);
    if (current.signature.service.status === "ATIVO") throw new AppError(ErrorCode.Validation, "Active integration", { safeMessage: "Desative a integração ICP antes de remover o segredo." });
    await this.secrets.remove(ICP_SECRET_NAME);
    const next: SystemSettings = { ...current, signature: { ...current.signature, secretMask: null, secretUpdatedAt: new Date().toISOString() } };
    await this.persist(context, next, version, "signature", [{ path: "signature.secret", previous: current.signature.secretMask ?? "—", next: "—" }], cleanReason);
    return this.getSettings(context);
  }

  // ═══════════════════════════════════════════════════════ integrações

  async listIntegrations(context: RequestContext): Promise<IntegrationView[]> {
    this.auth.require(context, PermissionCode.SettingsIntegrationRead);
    const s = await this.provider.get();
    const tests = await this.repo.listIntegrationTests();
    const icp = s.signature.service;
    const serverMode = this.probe.mode === "SERVIDOR";
    return [
      {
        id: "ICP", name: icp.name || "Assinatura Digital ICP", type: "Assinatura digital (ICP-Brasil)",
        status: icp.status === "ATIVO" ? "ATIVO" : "INATIVO", environment: icp.environment === "PRODUCAO" ? "Produção" : "Homologação",
        integrated: false, canDeactivate: icp.status === "ATIVO", editTab: "assinatura",
        note: "Nenhum provedor ICP está integrado nesta versão: a configuração é validada e guardada; os documentos continuam assinados pela imagem cadastrada do Profissional.",
        lastTest: tests["ICP"] ?? null
      },
      { id: "EMAIL", name: "E-mail", type: "Canal de notificação", status: "NÃO INTEGRADO", environment: "—", integrated: false, canDeactivate: false, editTab: null, note: "Não há serviço de e-mail no sistema; as notificações são internas.", lastTest: tests["EMAIL"] ?? null },
      { id: "STORAGE", name: "Armazenamento", type: "Persistência de dados e documentos", status: "ATIVO", environment: serverMode ? "Servidor (SQLite)" : "Navegador (memória + localStorage)", integrated: true, canDeactivate: false, editTab: null, note: "É o núcleo do sistema — não se desativa.", lastTest: tests["STORAGE"] ?? null },
      { id: "PDF", name: "Motor de PDF", type: "Geração de documentos (pdfmake)", status: "ATIVO", environment: "Local", integrated: true, canDeactivate: false, editTab: null, note: "Gera Visualização e PDF a partir do mesmo modelo documental — não se desativa.", lastTest: tests["PDF"] ?? null }
    ];
  }

  async testIntegration(context: RequestContext, id: IntegrationView["id"]): Promise<DiagnosticResult> {
    this.auth.require(context, PermissionCode.SettingsIntegrationUpdate);
    const result = id === "ICP" ? await this.icpCheck() : id === "STORAGE" ? await this.storageCheck() : id === "PDF" ? await this.pdfCheck() : id === "EMAIL"
      ? { id: "EMAIL", label: "E-mail", ok: false, status: "NÃO INTEGRADO", message: "Não há serviço de e-mail no sistema para testar.", checks: [] }
      : null;
    if (!result) throw new AppError(ErrorCode.Validation, "Unknown integration", { safeMessage: "Integração desconhecida." });
    await this.recordTest(context, id, result);
    return result;
  }

  /** "Testar conexão" da aba Assinatura — mesma verificação, permissão da ICP. */
  async testIcpConnection(context: RequestContext): Promise<DiagnosticResult> {
    this.auth.require(context, PermissionCode.SettingsSignatureUpdate);
    const result = await this.icpCheck();
    await this.recordTest(context, "ICP", result);
    return result;
  }

  async setIntegrationStatus(context: RequestContext, id: IntegrationView["id"], active: boolean, expectedVersion: number, reason: string): Promise<SettingsView> {
    this.auth.require(context, PermissionCode.SettingsIntegrationUpdate);
    if (id !== "ICP") throw new AppError(ErrorCode.Validation, "Not toggleable", { safeMessage: "Só a integração ICP pode ser ativada ou desativada; as demais são núcleo do sistema ou não estão integradas." });
    const cleanReason = this.requireReason("integration", reason);
    const { settings: current, version } = await this.provider.reload();
    this.assertVersion(expectedVersion, version);
    const nextService = { ...current.signature.service, status: active ? "ATIVO" as const : "INATIVO" as const };
    if (active) {
      const errors = validateSection("signature", { ...current.signature, service: nextService, secretMask: undefined, secretUpdatedAt: undefined, lastTest: undefined });
      if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Cannot activate", { safeMessage: errors.join(" ") });
      if (!(await this.secrets.has(ICP_SECRET_NAME))) throw new AppError(ErrorCode.Validation, "No secret", { safeMessage: "Cadastre o segredo (client secret) antes de ativar a integração ICP." });
    }
    if (nextService.status === current.signature.service.status) return this.getSettings(context);
    const next: SystemSettings = { ...current, signature: { ...current.signature, service: nextService } };
    await this.persist(context, next, version, "integration", [{ path: "signature.service.status", previous: current.signature.service.status, next: nextService.status }], cleanReason);
    return this.getSettings(context);
  }

  // ═══════════════════════════════════════════════════════ diagnóstico

  async getDiagnosticsInfo(context: RequestContext): Promise<{ appVersion: string; versionInfo: AppVersion; mode: string; database: Awaited<ReturnType<SettingsDiagnosticsProbe["database"]>>; services: CheckItem[] }> {
    this.auth.require(context, PermissionCode.SettingsDiagnosticsRead);
    const s = await this.provider.get();
    return {
      appVersion: this.probe.appVersion(),
      versionInfo: this.probe.versionInfo(),
      mode: this.probe.mode === "SERVIDOR" ? "Servidor" : "Navegador (teste local)",
      database: await this.probe.database(),
      services: [
        { label: "Permission Service", status: "OK", detail: "RBAC central (permissões do perfil + individuais)." },
        { label: "PDF Service", status: "OK", detail: "pdfmake local." },
        { label: "Armazenamento", status: "OK", detail: this.probe.mode === "SERVIDOR" ? "SQLite no servidor." : "Memória + localStorage do navegador." },
        { label: "Assinatura ICP", status: s.signature.service.status === "ATIVO" ? "AVISO" : "NÃO VERIFICADO", detail: s.signature.service.status === "ATIVO" ? "Configuração ativa; provedor não integrado." : "Inativa." },
        { label: "Cofre de segredos", status: this.secrets.available ? "OK" : "AVISO", detail: this.secrets.available ? "Disponível (cifrado no servidor)." : this.secrets.unavailableReason },
        { label: "Serviços externos", status: "NÃO VERIFICADO", detail: "Nenhum serviço externo integrado (e-mail, ICP)." }
      ]
    };
  }

  async runDiagnostic(context: RequestContext, check: "ALL" | "PDF" | "PERMISSION" | "STORAGE" | "ICP"): Promise<DiagnosticResult[]> {
    this.auth.require(context, PermissionCode.SettingsDiagnosticsExecute);
    const run: Record<string, () => Promise<DiagnosticResult>> = {
      PERMISSION: async () => this.permissionCheck(context),
      PDF: () => this.pdfCheck(),
      STORAGE: () => this.storageCheck(),
      ICP: () => this.icpCheck()
    };
    const ids = check === "ALL" ? ["PERMISSION", "PDF", "STORAGE", "ICP"] : [check];
    const results: DiagnosticResult[] = [];
    for (const id of ids) {
      const fn = run[id];
      if (!fn) throw new AppError(ErrorCode.Validation, "Unknown check", { safeMessage: "Verificação desconhecida." });
      try { results.push(await fn()); } catch (e) { results.push({ id, label: id, ok: false, status: "FALHA", message: (e as Error)?.message || "Falha ao executar a verificação.", checks: [] }); }
    }
    return results;
  }

  /** Relê as configurações do armazenamento (descarta o que estava em memória). */
  async reloadSettings(context: RequestContext): Promise<{ version: number; message: string }> {
    this.auth.require(context, PermissionCode.SettingsDiagnosticsExecute);
    this.provider.invalidate();
    const { version } = await this.provider.reload();
    return { version, message: `Configurações recarregadas do armazenamento (versão ${version}).` };
  }

  /** Limpa o cache de configurações do servidor. Não apaga dado nenhum. */
  async clearCache(context: RequestContext): Promise<{ message: string }> {
    this.auth.require(context, PermissionCode.SettingsDiagnosticsExecute);
    this.provider.invalidate();
    return { message: "Cache de configurações limpo. A próxima leitura vem do armazenamento. Nenhum dado foi apagado." };
  }

  /**
   * ZONA DE RISCO — Reset controlado de todos os dados operacionais da base.
   *
   * 1. Confere permissão estrita `system.database.reset` (exclusiva de Administrador).
   * 2. Exige frase de confirmação textual exata: "EXCLUIR TODOS OS DADOS".
   * 3. Reautentica no backend a senha atual do Administrador autenticado.
   * 4. Gera backup consistente do SQLite (VACUUM INTO) em /backups/ antes de qualquer delete.
   * 5. Valida integridade do backup (tamanho > 0 e integrity_check = ok).
   * 6. Executa a limpeza atômica em transação (preservando usr_admin, RBAC e schema).
   * 7. Registra auditoria com contagens apagadas (nunca logando a senha).
   */
  async resetOperationalDatabase(
    context: RequestContext,
    input: { currentAdminPassword?: string; confirmationPhrase?: string; reason?: string }
  ): Promise<DatabaseResetResult> {
    this.auth.require(context, PermissionCode.SystemDatabaseReset);

    // 1. Validação da frase textual obrigatória (Fase 18)
    const normalizedPhrase = String(input?.confirmationPhrase ?? "").trim().toUpperCase();
    if (normalizedPhrase !== "EXCLUIR TODOS OS DADOS") {
      throw new AppError(
        ErrorCode.Validation,
        "Frase de confirmação incorreta. Digite exatamente 'EXCLUIR TODOS OS DADOS' para prosseguir.",
        { safeMessage: "Frase de confirmação incorreta. Digite exatamente 'EXCLUIR TODOS OS DADOS'." }
      );
    }

    // 2. Reautenticação por senha no backend (Fase 17)
    const password = String(input?.currentAdminPassword ?? "").trim();
    if (!password) {
      throw new AppError(ErrorCode.Validation, "Senha do Administrador é obrigatória.", {
        safeMessage: "Informe sua senha atual de Administrador para confirmar a operação."
      });
    }

    if (!this.adminAuthResolver) {
      throw new AppError(ErrorCode.Internal, "Mecanismo de autenticação de senha não configurado.");
    }

    // Obter login/email do usuário autenticado no contexto
    let userIdentifier = "admin";
    if (this.userNameOf && context.userId) {
      userIdentifier = (await this.userNameOf(String(context.userId))) || "admin";
    }

    const auditContext: RequestContext = {
      ...context,
      correlationId: context.correlationId || (`cid-${Date.now()}` as any),
      userId: context.userId || ("usr_admin" as any)
    };

    const authResult = await this.adminAuthResolver(userIdentifier, password);
    if (!authResult || !authResult.user) {
      // Tentar também com 'admin' caso identifier não coincida
      const fallbackAuth = await this.adminAuthResolver("admin", password);
      if (!fallbackAuth || !fallbackAuth.user) {
        if (this.audit) {
          try {
            await this.audit.record(auditContext, {
              action: AuditAction.DatabaseResetFailed,
              entityType: "SystemDatabase",
              entityId: "OPERATIONAL_DATA",
              previousState: null,
              newState: null,
              metadata: { reason: "SENHA_INCORRETA", attemptedBy: context.userId }
            });
          } catch {}
        }
        throw new AppError(ErrorCode.Authorization, "Senha incorreta. Operação cancelada.", {
          safeMessage: "Senha do Administrador incorreta. A base não foi alterada."
        });
      }
    }

    // 3. Executar o reset com backup prévio obrigatório
    if (!this.databaseResetHandler) {
      throw new AppError(ErrorCode.Internal, "Manipulador de reset de banco não disponível neste ambiente.");
    }

    if (this.audit) {
      try {
        await this.audit.record(auditContext, {
          action: AuditAction.DatabaseResetRequested,
          entityType: "SystemDatabase",
          entityId: "OPERATIONAL_DATA",
          previousState: null,
          newState: null,
          metadata: { requestedBy: context.userId, reason: input.reason || "RESET_MANUAL" }
        });
      } catch {}
    }

    try {
      const result = await this.databaseResetHandler(context, input.reason);

      if (this.audit) {
        try {
          await this.audit.record(auditContext, {
            action: AuditAction.DatabaseResetCompleted,
            entityType: "SystemDatabase",
            entityId: "OPERATIONAL_DATA",
            previousState: null,
            newState: null,
            metadata: {
              executedBy: context.userId,
              backupPath: result.backupPath,
              backupSizeBytes: result.backupSizeBytes,
              deletedCounts: result.deletedCounts
            }
          });
        } catch {}
      }

      return result;
    } catch (err: any) {
      if (this.audit) {
        try {
          await this.audit.record(auditContext, {
            action: AuditAction.DatabaseResetFailed,
            entityType: "SystemDatabase",
            entityId: "OPERATIONAL_DATA",
            previousState: null,
            newState: null,
            metadata: { error: err?.message || String(err) }
          });
        } catch {}
      }
      throw err;
    }
  }

  // ═══════════════════════════════════════════════════════ internos

  private requireReason(section: SettingsSectionId, reason: string): string {
    const clean = String(reason ?? "").trim();
    if (SECTIONS_REQUIRING_REASON.has(section) && clean.length < 5) {
      throw new AppError(ErrorCode.Validation, "Reason required", { safeMessage: "Informe o motivo da alteração (mínimo 5 caracteres) — esta seção muda numeração, acesso, importação ou assinatura." });
    }
    return clean.slice(0, 500);
  }

  private assertVersion(expected: number, actual: number): void {
    if (!Number.isInteger(expected) || expected !== actual) {
      throw new AppError(ErrorCode.Conflict, "Settings version conflict", { safeMessage: "As configurações foram alteradas por outra pessoa depois que você abriu esta tela. Recarregue para ver a versão atual antes de salvar." });
    }
  }

  private async persist(context: RequestContext, next: SystemSettings, version: number, section: SettingsSectionId, changes: Array<{ path: string; previous: unknown; next: unknown }>, reason: string): Promise<void> {
    const now = new Date().toISOString();
    const userId = context.userId || "system";
    const saved = await this.provider.save(next, version, { updatedAt: now, updatedBy: userId });
    if (!saved) this.assertVersion(version, version + 1);
    const userName = (this.userNameOf ? await this.userNameOf(userId).catch(() => null) : null) || userId;
    const auditEvent = AUDIT_EVENT_OF_SECTION[section];
    const entries: SettingsHistoryEntry[] = changes.map(c => ({
      id: randomUUID(), at: now, userId, userName, section, path: c.path,
      previous: historyValue(c.previous), next: historyValue(c.next), reason: reason || "—", auditEvent
    }));
    await this.repo.appendHistory(entries);
    await this.audit?.record(context, {
      action: AuditAction.Update,
      entityType: "SystemSettings",
      entityId: "GLOBAL",
      previousState: { version } as JsonObject,
      newState: { version: version + 1 } as JsonObject,
      metadata: { event: auditEvent, section, reason: reason || null, changes: entries.map(e => ({ path: e.path, previous: e.previous, next: e.next })) } as unknown as JsonObject
    }).catch(() => null);
  }

  private async recordTest(context: RequestContext, id: string, result: DiagnosticResult): Promise<void> {
    const entry: IntegrationTestResult = { at: new Date().toISOString(), ok: result.ok, status: result.status, message: result.message, by: context.userId || "system" };
    // Registro operacional, fora da versão: testar não invalida a edição em curso de ninguém.
    await this.repo.recordIntegrationTest(id, entry);
  }

  private permissionCheck(context: RequestContext): DiagnosticResult {
    const probeAllowed = { userId: "diagnostic", permissions: new Set([PermissionCode.SettingsRead]) } as unknown as RequestContext;
    const probeDenied = { userId: "diagnostic", permissions: new Set<string>() } as unknown as RequestContext;
    const checks: CheckItem[] = [
      { label: "Permissão concedida é aceita", status: this.auth.can(probeAllowed, PermissionCode.SettingsRead) ? "OK" : "FALHA", detail: "Contexto com settings.read." },
      { label: "Permissão ausente é negada", status: !this.auth.can(probeDenied, PermissionCode.SettingsRead) ? "OK" : "FALHA", detail: "Contexto vazio não pode ler." },
      { label: "Sessão atual pode executar diagnóstico", status: this.auth.can(context, PermissionCode.SettingsDiagnosticsExecute) ? "OK" : "FALHA", detail: "settings.diagnostics.execute." }
    ];
    const ok = checks.every(c => c.status === "OK");
    return { id: "PERMISSION", label: "Permission Service", ok, status: ok ? "OK" : "FALHA", message: ok ? "Concede o que foi concedido e nega o que não foi." : "O serviço de permissões não respondeu como esperado.", checks };
  }

  private async pdfCheck(): Promise<DiagnosticResult> {
    const r = await this.probe.pdf();
    return { id: "PDF", label: "PDF Service", ok: r.ok, status: r.ok ? "OK" : "FALHA", message: r.message, checks: [] };
  }

  private async storageCheck(): Promise<DiagnosticResult> {
    const r = await this.repo.probe();
    return { id: "STORAGE", label: "Armazenamento", ok: r.ok, status: r.ok ? "OK" : "FALHA", message: r.message, checks: [] };
  }

  /**
   * TESTAR CONEXÃO ICP. Confere tudo o que pode ser conferido sem um
   * provedor integrado — e diz com todas as letras o que NÃO foi conferido.
   * Uma verificação que respondesse "conectado" sem ter falado com ninguém
   * seria pior que nenhuma.
   */
  private async icpCheck(): Promise<DiagnosticResult> {
    const s = (await this.provider.get()).signature;
    const hasSecret = await this.secrets.has(ICP_SECRET_NAME);
    const cnpj = s.company.cnpj;
    const endpoint = s.service.endpoint.trim();
    const checks: CheckItem[] = [
      { label: "CNPJ da empresa titular", status: cnpj && isValidCnpj(cnpj) ? "OK" : "FALHA", detail: cnpj ? (isValidCnpj(cnpj) ? formatCnpj(cnpj) : "CNPJ inválido.") : "Não informado." },
      { label: "Endpoint", status: /^https:\/\//i.test(endpoint) ? "OK" : "FALHA", detail: endpoint ? (/^https:\/\//i.test(endpoint) ? "Endereço https informado." : "O endpoint precisa ser https.") : "Não informado." },
      { label: "Credenciais", status: s.service.clientId.trim() && hasSecret ? "OK" : "FALHA", detail: !s.service.clientId.trim() ? "Client ID não informado." : hasSecret ? `Client ID e segredo cadastrados (${s.secretMask ?? "••••••••"}).` : (this.secrets.available ? "Segredo não cadastrado." : this.secrets.unavailableReason) },
      { label: "Ambiente", status: "OK", detail: s.service.environment === "PRODUCAO" ? "Produção." : "Homologação." },
      { label: "Autenticação no provedor", status: "NÃO VERIFICADO", detail: "Nenhum provedor ICP integrado nesta versão." },
      { label: "Disponibilidade do serviço", status: "NÃO VERIFICADO", detail: "Nenhum provedor ICP integrado nesta versão." }
    ];
    const configOk = checks.slice(0, 4).every(c => c.status === "OK");
    return {
      id: "ICP", label: "Assinatura ICP",
      ok: false,
      status: configOk ? "CONFIGURAÇÃO VÁLIDA — PROVEDOR NÃO INTEGRADO" : "CONFIGURAÇÃO INCOMPLETA",
      message: configOk
        ? "Os dados cadastrados estão completos e coerentes. A conexão com o provedor NÃO foi testada: não há adaptador de provedor ICP neste sistema."
        : "Corrija os itens com FALHA. A conexão com o provedor não é testada nesta versão.",
      checks
    };
  }
}
